import {
    useLayoutEffect,
    useState,
    useRef
} from "react";
import {
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../../../context";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type RootStackParamList from "../../../../../types/navigation";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    LockKeyhole,
    ChevronLeft,
    Trash2,
    Check,
    Mail,
    User
} from "lucide-react-native";
import OneSignalService from "../../../../../services/OneSignalService";
import {
    RESTService
} from "../../../../../services/restAPI";
import {
    storage
} from "../../../../../storage";

const DeleteAccount = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
    const logoutNavigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [globalState,
        setGlobalState] = useGlobalState();

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [reason,
        setReason] = useState("");
    const [deleteConfirm,
        setDeleteConfirm] = useState("");
    const [deleteStage,
        setDeleteStage] = useState<1|2>(1);

    const deleteAccountRef = useRef<IBottomSheetRef>(null);
    const deleteSuccess = useRef<IBottomSheetRef>(null);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("deleteAccount")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const deleteAccountRequest = () => {
        RESTService.action("DeleteMyAccount", {
            reason: reason
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    deleteAccountRef.current?.close();
                    NCoreUIKitSnackBar.open({
                        title: localize("accountDeleted"),
                        type: "success",
                        isShowAction: false,
                        autoCloseDelay: 10000,
                        icon: ({
                            color
                        }) => <Check
                            color={colors.content.icon[color ? color : "default"]}
                        />
                    });
                    deleteSuccess.current?.open();
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.contentCenter,
                {
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <View
                style={{
                    padding: spaces.spacingXl,
                    backgroundColor: colors.content.container.danger,
                    borderRadius: radiuses.full,
                    marginBottom:spaces.spacingLg
                }}
            >
                <Trash2
                    size={40}
                    color={colors.content.icon.danger}
                />
            </View>
            <Text
                variant="titleLargeSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingLg
                }}
            >
                {localize("confirmDeleteAccount")}
            </Text>
            <Text
                style={stylesheet.textCenter}
                variant="bodyLargeSize"
            >
                {localize("deleteAccountIrreversible")}
            </Text>
            <View
                style={[
                    stylesheet.deleteInfoContainer,
                    {
                        backgroundColor: colors.content.container.danger,
                        marginTop: spaces.spacingXl,
                        marginBottom: spaces.spacingXl,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                    variant="titleMediumSize"
                    color="danger"
                >
                    Silinecekler
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="danger"
                >
                    • Profil bilgilerin
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="danger"
                >
                    • Tüm belgeler ve kayıtlar
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="danger"
                >
                    • Bildirim geçmişin
                </Text>
            </View>
        </View>;
    };

    const renderInfoEmail = () => {
        return <View
            style={[
                stylesheet.infoRow,
                {
                    backgroundColor: colors.project.brandWhite,
                    paddingHorizontal: spaces.spacingLg,
                    paddingVertical: spaces.spacingMd,
                    borderWidth: borders.line,
                    borderColor: colors.content.border.subtle,
                    borderRadius: radiuses.md,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <Mail
                color={colors.content.icon.mid}
                style={{
                    marginRight: spaces.spacingMd
                }}
            />
            <View>
                <Text
                    variant="bodyLargeSize"
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                >
                    {localize("email")}
                </Text>
                <Text>
                    {globalState.user?.mail}
                </Text>
            </View>
        </View>;
    };

    const renderMembership = () => {
        return <View
            style={[
                stylesheet.infoRow,
                {
                    backgroundColor: colors.project.brandWhite,
                    paddingHorizontal: spaces.spacingLg,
                    paddingVertical: spaces.spacingMd,
                    borderWidth: borders.line,
                    borderColor: colors.content.border.subtle,
                    borderRadius: radiuses.md,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <User
                color={colors.content.icon.mid}
                style={{
                    marginRight: spaces.spacingMd
                }}
            />
            <View>
                <Text
                    variant="bodyLargeSize"
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                >
                    {localize("membership")}
                </Text>
                <Text>
                    Genç MÜSİAD Konya Üyesi
                </Text>
            </View>
        </View>;
    };

    const renderActions = () => {
        return <View>
            <Button
                onPress={() => {
                    deleteAccountRef.current?.open();
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                title={localize("deleteAccount")}
                customTextColor={colors.project.brandWhite}
                customBorderColor="black"
                spreadBehaviour="stretch"
                customColor="black"
                size="large"
            />
            <Button
                title={localize("abandon")}
                customBorderColor="black"
                onPress={() => {
                    navigation.goBack();
                }}
                variant="outline"
                spreadBehaviour="stretch"
                size="large"
            />
        </View>;
    };

    const renderDeleteSheetContent = () => {
        if(deleteStage === 1) {
            return <View
                style={[
                    stylesheet.contentCenter,
                    {
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <View
                    style={{
                        padding: spaces.spacingLg,
                        backgroundColor: colors.content.container.danger,
                        borderRadius: radiuses.full,
                        marginBottom: spaces.spacingMd
                    }}
                >
                    <LockKeyhole
                        color={colors.content.icon.danger}
                        size={30}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("deleteReasonTitle")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("deleteReasonDescription")}
                </Text>
                <TextInput
                    type={reason.length > 0 && reason.length < 10 ? "danger" : "default"}
                    hintText={reason.length > 0 && reason.length < 10 ? (localize("reason-too-short")) : undefined}
                    key={"deletereason"}
                    spreadBehaviour="stretch"
                    placeholder={localize("deleteReasonPlaceholder")}
                    onChangeText={(e) => {
                        setReason(e);
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <View
                    style={[
                        stylesheet.row,
                        {
                            marginBottom: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={{
                            width: spaces.spacingMd,
                            height: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            backgroundColor:colors.content.icon.danger,
                            marginRight: spaces.spacingMd
                        }}
                    />
                    <View
                        style={{
                            width: spaces.spacingMd,
                            height: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            backgroundColor: colors.content.container.overlay
                        }}
                    />
                </View>
                <Button
                    title={localize("continue")}
                    customTextColor={colors.project.brandWhite}
                    customColor="black"
                    customBorderColor="black"
                    onPress={() => {
                        if (reason.length < 10) {
                            NCoreUIKitToast.open({
                                title: localize("error"),
                                subTitle: localize("reason-too-short"),
                                type: "danger"
                            });
                            return;
                        }
                        setDeleteStage(2);
                    }}
                    isDisabled={reason.length <10 ? true: false}
                    spreadBehaviour="stretch"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <Button
                    title={localize("abandon")}
                    customBorderColor="black"
                    onPress={() => {
                        deleteAccountRef.current?.close();
                    }}
                    variant="outline"
                    spreadBehaviour="stretch"
                />
            </View>;
        }
        if(deleteStage === 2) {
            return <View
                style={[
                    stylesheet.contentCenter,
                    {
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <View
                    style={{
                        padding: spaces.spacingLg,
                        backgroundColor: colors.content.container.danger,
                        borderRadius: radiuses.full,
                        marginBottom: spaces.spacingMd
                    }}
                >
                    <LockKeyhole
                        color={colors.content.icon.danger}
                        size={30}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("finalStepConfirmationTitle")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("finalStepInstruction")}
                </Text>
                <TextInput
                    type={deleteConfirm.length > 0 && deleteConfirm !== localize("confirmDeleteWord") ? "danger" : "default"}
                    hintText={deleteConfirm.length > 0 && deleteConfirm !== localize("confirmDeleteWord") ? (localize("invalid-confirm-word")) : undefined}
                    key={"deleteconfirm"}
                    spreadBehaviour="stretch"
                    placeholder={localize("confirmDeleteWord")}
                    onChangeText={(e) => {
                        setDeleteConfirm(e);
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <View
                    style={[
                        stylesheet.row,
                        {
                            marginBottom: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={{
                            width: spaces.spacingMd,
                            height: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            backgroundColor:colors.content.container.overlay,
                            marginRight: spaces.spacingMd
                        }}
                    />
                    <View
                        style={{
                            width: spaces.spacingMd,
                            height: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            backgroundColor: colors.content.icon.danger
                        }}
                    />
                </View>
                <Button
                    onPress={() => {
                        if (deleteConfirm !== localize("confirmDeleteWord")) {
                            NCoreUIKitToast.open({
                                title: localize("error"),
                                subTitle: localize("invalid-confirm-word"),
                                type: "danger"
                            });
                            return;
                        }
                        deleteAccountRequest();
                    }}
                    isDisabled={deleteConfirm === localize("confirmDeleteWord") ? false: true}
                    customTextColor={colors.project.brandWhite}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                    title={localize("deleteAccount")}
                    customBorderColor="black"
                    spreadBehaviour="stretch"
                    customColor="black"
                />
                <Button
                    title={localize("abandon")}
                    customBorderColor="black"
                    onPress={() => {
                        deleteAccountRef.current?.close();
                    }}
                    variant="outline"
                    spreadBehaviour="stretch"
                />
            </View>;
        }
        else {
            return null;
        }
    };

    const renderDeleteSheet = () => {
        return <BottomSheet
            ref={deleteAccountRef}
            isAutoHeight={true}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            backgroundColor="default"
            onClose={() => {
                setDeleteStage(1);
                setReason("");
            }}
            scrollViewProps={{
                contentContainerStyle: {
                    flex: 1
                }
            }}
        >
            {renderDeleteSheetContent()}
        </BottomSheet>;
    };

    const renderDeleteSuccess = () => {
        return <BottomSheet
            ref={deleteSuccess}
            isAutoHeight={true}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            backgroundColor="default"
            onClose={() => {
                OneSignalService.logout();

                storage.clearAll();
                setGlobalState({
                    isAppReady: true,
                    isGuest: true,
                    user: null
                });

                logoutNavigation.reset({
                    index: 0,
                    routes: [
                        {
                            name: "Auth"
                        }
                    ]
                });
            }}
            scrollViewProps={{
                contentContainerStyle: {
                    flex: 1
                }
            }}
        >
            <View
                style={[
                    stylesheet.contentCenter,
                    {
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <View
                    style={{
                        padding: spaces.spacingLg,
                        backgroundColor: colors.content.container.success,
                        borderRadius: radiuses.full,
                        marginBottom: spaces.spacingMd
                    }}
                >
                    <Check
                        color={colors.content.icon.success}
                        size={30}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("accountDeletedDone")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("accountDeletionCompleted")}
                </Text>

                <Button
                    onPress={() => {
                        deleteSuccess.current?.close();
                    }}
                    customTextColor={colors.project.brandWhite}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                    customBorderColor="black"
                    spreadBehaviour="stretch"
                    customColor="black"
                    title="Kapat"
                />
            </View>
        </BottomSheet>;
    };

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        <View>
            {renderHeader()}
            {renderInfoEmail()}
            {renderMembership()}
        </View>
        {renderActions()}
        {renderDeleteSheet()}
        {renderDeleteSuccess()}
    </PageContainer>;
};
export default DeleteAccount;
